import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Modal } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { InputNumber } from 'primereact/inputnumber'
import { MultiSelect } from 'primereact/multiselect'
import { ProgressSpinner } from 'primereact/progressspinner'
import { RadioButton } from 'primereact/radiobutton'
import {
  ContractHonorariumCreateResultDTO,
  ContractHonorariumDetailDTO,
  ContractHonorariumFormErrors,
  ContractHonorariumFormField,
  ContractHonorariumOptionDTO,
  ContractHonorariumOptionsDTO,
  ContractHonorariumPlanOptionDTO,
  ContractHonorariumValuesDTO,
} from '../../dto/contractHonorarium/ContractHonorariumDTO'
import { EDistributionContractType } from '../../enums/EDistributionContractType'
import {
  createContractHonorariums,
  getContractHonorarium,
  getContractHonorariumPlans,
  searchContractHonorariumNomenclators,
  updateContractHonorarium,
} from '../../services/ContractHonorariumController'
import { ApiErrorResponse, parseApiError } from '../../services/apiError'
import { fromDateToString } from '../../utils/utils'
import DuplicateCombinationsModal from './components/DuplicateCombinationsModal'
import PercentageInput from './components/PercentageInput'
import { isFixedAmount, nameOrGeneral, parseIsoDate, RETENTION_CONTRACT_HELP, STATUS_LABELS, STATUS_TAG_CLASSES } from './contractHonorariumFormat'

interface ContractHonorariumFormViewProps {
  contractHonorariumId: number | null
  options: ContractHonorariumOptionsDTO
  onBack: () => void
  onCreated: (result: ContractHonorariumCreateResultDTO) => void
  onUpdated: (updated: boolean) => void
  onError: (message: string) => void
  onStale: (message: string) => void
}

interface FormState {
  billingEntityIds: number[]
  healthEntityIds: number[]
  healthEntityPlanIds: number[]
  professionalIds: number[]
  nomenclators: ContractHonorariumOptionDTO[]
  buildingIds: number[]
  distributionTypeId: number | null
  honoPercentage: number | null
  copaymentPercentage: number | null
  diferentialPercentage: number | null
  fixedAmount: number | null
  validFrom: Date | null
  validUntil: Date | null
}

type EditableField = keyof FormState

const MESSAGES = {
  billingEntityRequired: 'Seleccioná al menos un emisor de facturación',
  distributionTypeRequired: 'Elegí el tipo de distribución',
  honoPercentageRequired: 'Completá el porcentaje de honorarios',
  copaymentPercentageRequired: 'Completá el porcentaje de coseguro',
  diferentialPercentageRequired: 'Completá el porcentaje de diferencial',
  percentageOutOfRange: 'Ingresá un porcentaje entre 0 y 100',
  fixedAmountRequired: 'Completá el importe fijo',
  fixedAmountNegative: 'Ingresá un importe igual o mayor a 0',
  validFromRequired: 'Completá la fecha desde',
  validUntilBeforeValidFrom: 'Ingresá una fecha hasta igual o posterior a la fecha desde',
  futureMustStayFuture: 'Ingresá una fecha desde posterior a hoy: la distribución todavía no entró en vigencia',
}

const FIELD_INPUT_IDS: Record<ContractHonorariumFormField, string> = {
  billingEntityIds: 'chd-billing-entities',
  healthEntityIds: 'chd-health-entities',
  healthEntityPlanIds: 'chd-health-entity-plans',
  professionalIds: 'chd-professionals',
  nomenclatorIds: 'chd-nomenclators',
  buildingIds: 'chd-buildings',
  distributionTypeId: `chd-distribution-type-${EDistributionContractType.PERCENTAGE}`,
  honoPercentage: 'chd-hono-percentage',
  copaymentPercentage: 'chd-copayment-percentage',
  diferentialPercentage: 'chd-diferential-percentage',
  fixedAmount: 'chd-fixed-amount',
  validFrom: 'chd-valid-from',
  validUntil: 'chd-valid-until',
  combinations: 'chd-billing-entities',
}

const FIELD_ORDER: ContractHonorariumFormField[] = [
  'billingEntityIds', 'healthEntityIds', 'healthEntityPlanIds', 'professionalIds', 'nomenclatorIds', 'buildingIds',
  'distributionTypeId', 'honoPercentage', 'copaymentPercentage', 'diferentialPercentage', 'fixedAmount',
  'validFrom', 'validUntil', 'combinations',
]
const API_FIELDS = new Set<string>(FIELD_ORDER)

const NOT_FOCUSABLE: ContractHonorariumFormField[] = ['combinations', 'validFrom', 'validUntil']

function isFormField(field: string): field is ContractHonorariumFormField {
  return API_FIELDS.has(field)
}

function today(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function emptyForm(): FormState {
  return {
    billingEntityIds: [],
    healthEntityIds: [],
    healthEntityPlanIds: [],
    professionalIds: [],
    nomenclators: [],
    buildingIds: [],
    distributionTypeId: EDistributionContractType.PERCENTAGE,
    honoPercentage: null,
    copaymentPercentage: null,
    diferentialPercentage: null,
    fixedAmount: null,
    validFrom: today(),
    validUntil: null,
  }
}

function formFromDetail(detail: ContractHonorariumDetailDTO): FormState {
  const fixed = isFixedAmount(detail.distributionTypeId)
  return {
    ...emptyForm(),
    distributionTypeId: detail.distributionTypeId,
    honoPercentage: fixed ? null : detail.honoPercentage,
    copaymentPercentage: fixed ? null : detail.copaymentPercentage,
    diferentialPercentage: fixed ? null : detail.diferentialPercentage,
    fixedAmount: fixed ? detail.fixedAmount : null,
    validFrom: parseIsoDate(detail.validFrom),
    validUntil: detail.validUntil ? parseIsoDate(detail.validUntil) : null,
  }
}

function validatePercentage(value: number | null, requiredMessage: string): string | undefined {
  if (value == null) return requiredMessage
  if (value < 0 || value > 100) return MESSAGES.percentageOutOfRange
  return undefined
}

function validate(form: FormState, isEdition: boolean, mustStayFuture: boolean): ContractHonorariumFormErrors {
  const errors: ContractHonorariumFormErrors = {}
  if (!isEdition && form.billingEntityIds.length === 0) errors.billingEntityIds = MESSAGES.billingEntityRequired

  if (form.distributionTypeId == null) {
    errors.distributionTypeId = MESSAGES.distributionTypeRequired
  } else if (isFixedAmount(form.distributionTypeId)) {
    if (form.fixedAmount == null) errors.fixedAmount = MESSAGES.fixedAmountRequired
    else if (form.fixedAmount < 0) errors.fixedAmount = MESSAGES.fixedAmountNegative
  } else {
    errors.honoPercentage = validatePercentage(form.honoPercentage, MESSAGES.honoPercentageRequired)
    errors.copaymentPercentage = validatePercentage(form.copaymentPercentage, MESSAGES.copaymentPercentageRequired)
    errors.diferentialPercentage = validatePercentage(form.diferentialPercentage, MESSAGES.diferentialPercentageRequired)
  }

  if (form.validFrom == null) {
    errors.validFrom = MESSAGES.validFromRequired
  } else if (mustStayFuture && form.validFrom <= today()) {
    errors.validFrom = MESSAGES.futureMustStayFuture
  } else if (form.validUntil != null && form.validUntil < form.validFrom) {
    errors.validUntil = MESSAGES.validUntilBeforeValidFrom
  }

  return Object.fromEntries(Object.entries(errors).filter(([, message]) => message)) as ContractHonorariumFormErrors
}

function countCombinations(form: FormState, plans: ContractHonorariumPlanOptionDTO[]): number {
  const healthTargets = form.healthEntityIds.length === 0
    ? 1
    : form.healthEntityIds.reduce((total, healthEntityId) => {
      const selectedPlans = plans.filter((plan) =>
        plan.healthEntityId === healthEntityId && form.healthEntityPlanIds.includes(plan.id)).length
      return total + Math.max(selectedPlans, 1)
    }, 0)
  return Math.max(form.billingEntityIds.length, 1) * healthTargets
    * Math.max(form.professionalIds.length, 1) * Math.max(form.nomenclators.length, 1)
    * Math.max(form.buildingIds.length, 1)
}

function toFieldErrors(apiError: ApiErrorResponse): ContractHonorariumFormErrors {
  const errors: ContractHonorariumFormErrors = {}
  for (const detail of apiError.details ?? []) {
    if (isFormField(detail.field) && !errors[detail.field]) {
      errors[detail.field] = detail.message
    }
  }
  return errors
}

function focusFirstError(errors: ContractHonorariumFormErrors) {
  const first = FIELD_ORDER.find((field) => errors[field])
  if (!first) return
  window.setTimeout(() => {
    const element = document.getElementById(FIELD_INPUT_IDS[first])
    if (!(element instanceof HTMLElement)) return
    element.scrollIntoView({ block: 'center' })
    if (!NOT_FOCUSABLE.includes(first)) element.focus()
  }, 0)
}

function chipSelectClassName(error: string | undefined): string {
  return error ? 'chd-chip-select p-invalid' : 'chd-chip-select'
}

function isOption(value: unknown): value is ContractHonorariumOptionDTO {
  return typeof value === 'object' && value !== null
    && typeof (value as ContractHonorariumOptionDTO).id === 'number'
    && typeof (value as ContractHonorariumOptionDTO).name === 'string'
}

function FieldError({ id, message }: Readonly<{ id: string; message?: string }>) {
  if (!message) return null
  return (
    <small id={id} className='chd-field-error'>
      <i className='pi pi-exclamation-triangle' aria-hidden='true' />
      {message}
    </small>
  )
}

function ReadOnlyData({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className='md-readonly'>
      <span className='md-readonly__label'>{label}</span>
      <span className='md-readonly__value'>{value || '—'}</span>
    </div>
  )
}

function snapshotOf(form: FormState): string {
  return JSON.stringify({
    ...form,
    nomenclators: form.nomenclators.map((nomenclator) => nomenclator.id),
    validFrom: form.validFrom ? fromDateToString(form.validFrom) : null,
    validUntil: form.validUntil ? fromDateToString(form.validUntil) : null,
  })
}

export default function ContractHonorariumFormView({
  contractHonorariumId,
  options,
  onBack,
  onCreated,
  onUpdated,
  onError,
  onStale,
}: Readonly<ContractHonorariumFormViewProps>) {
  const isEdition = contractHonorariumId != null
  const [detail, setDetail] = useState<ContractHonorariumDetailDTO | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(isEdition)
  const [form, setForm] = useState<FormState>(emptyForm)
  const initialSnapshot = useRef(snapshotOf(form))
  const latestForm = useRef(form)
  latestForm.current = form
  const [errors, setErrors] = useState<ContractHonorariumFormErrors>({})
  const [plans, setPlans] = useState<ContractHonorariumPlanOptionDTO[]>([])
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [nomenclatorResults, setNomenclatorResults] = useState<ContractHonorariumOptionDTO[]>([])
  const [searchingNomenclators, setSearchingNomenclators] = useState(false)
  const nomenclatorSearchTimer = useRef<number | undefined>(undefined)
  const nomenclatorSearchSeq = useRef(0)
  const [saving, setSaving] = useState(false)
  const [confirmingLeave, setConfirmingLeave] = useState(false)
  const [nothingCreated, setNothingCreated] = useState<ContractHonorariumCreateResultDTO | null>(null)
  const [viewingDuplicates, setViewingDuplicates] = useState(false)

  const mustStayFuture = detail?.status === 'FUTURE'

  useEffect(() => {
    if (contractHonorariumId == null) return
    let cancelled = false
    getContractHonorarium(contractHonorariumId)
      .then((loaded) => {
        if (cancelled) return
        const loadedForm = formFromDetail(loaded)
        setDetail(loaded)
        setForm(loadedForm)
        initialSnapshot.current = snapshotOf(loadedForm)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const apiError = parseApiError(error)
        if (apiError.errorCode === 'CONTRACT_HONORARIUM_NOT_FOUND') onStale(apiError.message)
        else {
          onError('No se pudo cargar la distribución. Intentá de nuevo.')
          onBack()
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false)
      })
    return () => {
      cancelled = true
    }
  }, [contractHonorariumId])

  const healthEntityKey = form.healthEntityIds.join(',')
  useEffect(() => {
    if (isEdition) return
    if (form.healthEntityIds.length === 0) {
      setPlans([])
      setForm((current) => (current.healthEntityPlanIds.length === 0 ? current : { ...current, healthEntityPlanIds: [] }))
      return
    }

    let cancelled = false
    setLoadingPlans(true)
    getContractHonorariumPlans(form.healthEntityIds)
      .then((loaded) => {
        if (cancelled) return
        setPlans(loaded)
        setForm((current) => {
          const valid = current.healthEntityPlanIds.filter((id) => loaded.some((plan) => plan.id === id))
          return valid.length === current.healthEntityPlanIds.length ? current : { ...current, healthEntityPlanIds: valid }
        })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        parseApiError(error)
        setPlans([])
        onError('No se pudieron cargar los planes de las obras sociales. Intentá de nuevo.')
      })
      .finally(() => {
        if (!cancelled) setLoadingPlans(false)
      })

    return () => {
      cancelled = true
    }
  }, [isEdition, healthEntityKey])

  const planOptions = useMemo(() => {
    const showHealthEntity = form.healthEntityIds.length > 1
    return plans.map((plan) => ({
      id: plan.id,
      label: showHealthEntity ? `${plan.name} (${plan.healthEntityName})` : plan.name,
    }))
  }, [plans, form.healthEntityIds.length])

  const nomenclatorOptions = useMemo(() => {
    const selectedIds = new Set(form.nomenclators.map((nomenclator) => nomenclator.id))
    return [...form.nomenclators, ...nomenclatorResults.filter((nomenclator) => !selectedIds.has(nomenclator.id))]
  }, [form.nomenclators, nomenclatorResults])

  useEffect(() => () => window.clearTimeout(nomenclatorSearchTimer.current), [])

  const combinations = useMemo(() => countCombinations(form, plans), [form, plans])
  const fixedAmountSelected = isFixedAmount(form.distributionTypeId)

  function updateField<K extends EditableField>(field: K, value: FormState[K]) {
    const next: FormState = { ...form, [field]: value }
    setForm(next)
    setNothingCreated(null)

    setErrors((current) => {
      const recomputed = validate(next, isEdition, mustStayFuture)
      const updated: ContractHonorariumFormErrors = { ...current }
      const related: ContractHonorariumFormField[] = field === 'nomenclators'
        ? ['nomenclatorIds']
        : field === 'validFrom' || field === 'validUntil'
          ? ['validFrom', 'validUntil']
          : field === 'distributionTypeId'
            ? ['distributionTypeId', 'honoPercentage', 'copaymentPercentage', 'diferentialPercentage', 'fixedAmount']
            : [field as ContractHonorariumFormField]
      let changed = Boolean(current.combinations)
      for (const relatedField of related) {
        if (current[relatedField] && current[relatedField] !== recomputed[relatedField]) {
          updated[relatedField] = recomputed[relatedField]
          changed = true
        }
      }
      updated.combinations = undefined
      return changed ? updated : current
    })
  }

   function validateOnBlur(field: ContractHonorariumFormField) {
    window.setTimeout(() => {
      const message = validate(latestForm.current, isEdition, mustStayFuture)[field]
      setErrors((current) => (current[field] === message ? current : { ...current, [field]: message }))
    }, 0)
  }

  async function searchNomenclators(query: string) {
    window.clearTimeout(nomenclatorSearchTimer.current)
    const seq = ++nomenclatorSearchSeq.current
    setSearchingNomenclators(true)
    try {
      const found = await searchContractHonorariumNomenclators(query.trim())
      if (seq === nomenclatorSearchSeq.current) setNomenclatorResults(found)
    } catch (error: unknown) {
      parseApiError(error)
      if (seq !== nomenclatorSearchSeq.current) return
      setNomenclatorResults([])
      onError('No se pudieron buscar los nomencladores. Intentá de nuevo.')
    } finally {
      if (seq === nomenclatorSearchSeq.current) setSearchingNomenclators(false)
    }
  }

  function scheduleNomenclatorSearch(query: string) {
    window.clearTimeout(nomenclatorSearchTimer.current)
    nomenclatorSearchTimer.current = window.setTimeout(() => { void searchNomenclators(query) }, 300)
  }

  function changeNomenclators(ids: number[]) {
    const byId = new Map(nomenclatorOptions.map((nomenclator) => [nomenclator.id, nomenclator]))
    updateField('nomenclators', ids.map((id) => byId.get(id)).filter(isOption))
  }

  function valuesPayload(): ContractHonorariumValuesDTO {
    const fixed = isFixedAmount(form.distributionTypeId)
    return {
      distributionTypeId: form.distributionTypeId,
      honoPercentage: fixed ? null : form.honoPercentage,
      copaymentPercentage: fixed ? null : form.copaymentPercentage,
      diferentialPercentage: fixed ? null : form.diferentialPercentage,
      fixedAmount: fixed ? form.fixedAmount : null,
      validFrom: fromDateToString(form.validFrom as Date),
      validUntil: form.validUntil ? fromDateToString(form.validUntil) : null,
    }
  }

  async function handleSave() {
    if (saving) return
    const found = validate(form, isEdition, mustStayFuture)
    if (Object.keys(found).length > 0) {
      setErrors(found)
      focusFirstError(found)
      return
    }

    if (contractHonorariumId != null && snapshotOf(form) === initialSnapshot.current) {
      onUpdated(false)
      return
    }

    setSaving(true)
    try {
      if (contractHonorariumId != null) {
        const updated = await updateContractHonorarium(contractHonorariumId, valuesPayload())
        onUpdated(updated)
      } else {
        const result = await createContractHonorariums({
          ...valuesPayload(),
          billingEntityIds: form.billingEntityIds,
          healthEntityIds: form.healthEntityIds,
          healthEntityPlanIds: form.healthEntityPlanIds,
          professionalIds: form.professionalIds,
          nomenclatorIds: form.nomenclators.map((nomenclator) => nomenclator.id),
          buildingIds: form.buildingIds,
        })
        if (result.created > 0) {
          onCreated(result)
        } else {
          setNothingCreated(result)
        }
      }
    } catch (error: unknown) {
      const apiError = parseApiError(error)
      const fieldErrors = toFieldErrors(apiError)
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors)
        focusFirstError(fieldErrors)
      } else if (apiError.errorCode === 'CONTRACT_HONORARIUM_NOT_FOUND') {
        onStale(apiError.message)
      } else if (apiError.errorCode === 'CONTRACT_HONORARIUM_CONCURRENT_CHANGE') {
        onError(apiError.message)
      } else {
        onError(isEdition
          ? 'No se pudieron guardar los cambios. Intentá de nuevo.'
          : 'No se pudo crear la distribución. Intentá de nuevo.')
      }
    } finally {
      setSaving(false)
    }
  }

  function fieldA11y(field: ContractHonorariumFormField, errorId: string) {
    return errors[field] ? { 'aria-invalid': true, 'aria-describedby': errorId } : {}
  }

  function requestLeave() {
    if (saving) return
    if (loadingDetail || snapshotOf(form) === initialSnapshot.current) {
      onBack()
    } else {
      setConfirmingLeave(true)
    }
  }

  function percentageField(
    field: 'honoPercentage' | 'copaymentPercentage' | 'diferentialPercentage',
    label: string,
    errorId: string,
  ) {
    return (
      <div className='chd-field'>
        <label htmlFor={FIELD_INPUT_IDS[field]}>
          {label} <span className='md-req'>*</span>
        </label>
        <PercentageInput
          inputId={FIELD_INPUT_IDS[field]}
          value={form[field]}
          invalid={Boolean(errors[field])}
          onChange={(value) => updateField(field, value)}
          onBlur={() => validateOnBlur(field)}
          {...fieldA11y(field, errorId)}
        />
        <FieldError id={errorId} message={errors[field]} />
      </div>
    )
  }

  const title = isEdition ? 'Editar distribución' : 'Nueva distribución'

  if (loadingDetail) {
    return (
      <div className='nv-scope nv-page chd-view chd-form-page'>
        <header className='nv-page-head'>
          <Button type='button' link icon='pi pi-angle-double-left' label='Volver' onClick={onBack} />
          <h1 className='nv-h1'>{title}</h1>
        </header>
        <div className='md-loading' role='status'>
          <ProgressSpinner strokeWidth='4' />
          <span>Cargando…</span>
        </div>
      </div>
    )
  }

  return (
    <div className='nv-scope nv-page chd-view chd-form-page'>
      <header className='nv-page-head'>
        <Button
          type='button'
          link
          icon='pi pi-angle-double-left'
          label='Volver'
          onClick={requestLeave}
        />
        <h1 className='nv-h1'>{title}</h1>
        {detail && (
          <span className='nv-subtitle'>
            <span className={STATUS_TAG_CLASSES[detail.status]}>{STATUS_LABELS[detail.status]}</span>
          </span>
        )}
      </header>

      <form
        className='chd-form'
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void handleSave()
        }}
      >
        <h2 className='md-section-title'>Alcance</h2>
        {detail ? (
          <div className='chd-readonly-row'>
            <ReadOnlyData label='Emisor de facturación' value={nameOrGeneral(detail.billingEntityName)} />
            <ReadOnlyData label='Obra social' value={nameOrGeneral(detail.healthEntityName)} />
            <ReadOnlyData label='Plan' value={nameOrGeneral(detail.healthEntityPlanName)} />
            <ReadOnlyData label='Profesional' value={nameOrGeneral(detail.professionalName)} />
            <ReadOnlyData label='Nomenclador' value={nameOrGeneral(detail.nomenclatorName)} />
            <ReadOnlyData label='Edificio' value={nameOrGeneral(detail.buildingName)} />
          </div>
        ) : null}
        {detail?.billingEntityProfessional && (
          <span className='chd-hint'>
            <i className='pi pi-info-circle' aria-hidden='true' />
            {RETENTION_CONTRACT_HELP}
          </span>
        )}
        {detail ? null : (
          <>
            <div className='chd-row chd-row-wide'>
              <div className='chd-field'>
                <label htmlFor={FIELD_INPUT_IDS.billingEntityIds}>
                  Emisores de facturación <span className='md-req'>*</span>
                </label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.billingEntityIds}
                  value={form.billingEntityIds}
                  options={options.billingEntities}
                  optionLabel='name'
                  optionValue='id'
                  display='chip'
                  filter
                  filterBy='name'
                  resetFilterOnHide
                  showSelectAll
                  emptyFilterMessage='No hay emisores que coincidan'
                  virtualScrollerOptions={options.billingEntities.length > 50 ? { itemSize: 38 } : undefined}
                  placeholder='Seleccionar'
                  className={chipSelectClassName(errors.billingEntityIds)}
                  onChange={(event) => updateField('billingEntityIds', (event.value as number[] | null) ?? [])}
                  onHide={() => validateOnBlur('billingEntityIds')}
                  {...fieldA11y('billingEntityIds', 'chd-billing-entities-error')}
                />
                <FieldError id='chd-billing-entities-error' message={errors.billingEntityIds} />
                {/* Un profesional como emisor hace el contrato de retención: se avisa qué significa */}
                {options.billingEntities.some(option => option.professional && form.billingEntityIds.includes(option.id)) && (
                  <span className='chd-hint' aria-live='polite'>
                    <i className='pi pi-info-circle' aria-hidden='true' />
                    {RETENTION_CONTRACT_HELP}
                  </span>
                )}
              </div>

              <div className='chd-field'>
                <label htmlFor={FIELD_INPUT_IDS.healthEntityIds}>Obras sociales</label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.healthEntityIds}
                  value={form.healthEntityIds}
                  options={options.healthEntities}
                  optionLabel='name'
                  optionValue='id'
                  display='chip'
                  filter
                  filterBy='name'
                  resetFilterOnHide
                  showSelectAll
                  emptyFilterMessage='No hay obras sociales que coincidan'
                  virtualScrollerOptions={options.healthEntities.length > 50 ? { itemSize: 38 } : undefined}
                  placeholder='Todas'
                  className={chipSelectClassName(errors.healthEntityIds)}
                  onChange={(event) => updateField('healthEntityIds', (event.value as number[] | null) ?? [])}
                  {...fieldA11y('healthEntityIds', 'chd-health-entities-error')}
                />
                <FieldError id='chd-health-entities-error' message={errors.healthEntityIds} />
              </div>
            </div>

            <div className='chd-row chd-row-wide'>
              <div className='chd-field'>
                <label htmlFor={FIELD_INPUT_IDS.healthEntityPlanIds}>Planes</label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.healthEntityPlanIds}
                  value={form.healthEntityPlanIds}
                  options={planOptions}
                  optionLabel='label'
                  optionValue='id'
                  display='chip'
                  filter={planOptions.length > 10}
                  filterBy='label'
                  showSelectAll
                  disabled={form.healthEntityIds.length === 0 || loadingPlans}
                  placeholder={loadingPlans ? 'Cargando planes…' : 'Todos'}
                  className={chipSelectClassName(errors.healthEntityPlanIds)}
                  onChange={(event) => updateField('healthEntityPlanIds', (event.value as number[] | null) ?? [])}
                  {...fieldA11y('healthEntityPlanIds', 'chd-health-entity-plans-error')}
                />
                <FieldError id='chd-health-entity-plans-error' message={errors.healthEntityPlanIds} />
              </div>

              <div className='chd-field'>
                <label htmlFor={FIELD_INPUT_IDS.professionalIds}>Profesionales</label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.professionalIds}
                  value={form.professionalIds}
                  options={options.professionals}
                  optionLabel='name'
                  optionValue='id'
                  display='chip'
                  filter
                  filterBy='name'
                  resetFilterOnHide
                  showSelectAll
                  emptyFilterMessage='No hay profesionales que coincidan'
                  virtualScrollerOptions={options.professionals.length > 50 ? { itemSize: 38 } : undefined}
                  placeholder='Todos'
                  className={chipSelectClassName(errors.professionalIds)}
                  onChange={(event) => updateField('professionalIds', (event.value as number[] | null) ?? [])}
                  {...fieldA11y('professionalIds', 'chd-professionals-error')}
                />
                <FieldError id='chd-professionals-error' message={errors.professionalIds} />
              </div>
            </div>

            <div className='chd-row chd-row-wide'>
              <div className='chd-field'>
                <label htmlFor={FIELD_INPUT_IDS.nomenclatorIds}>Nomencladores</label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.nomenclatorIds}
                  value={form.nomenclators.map((nomenclator) => nomenclator.id)}
                  options={nomenclatorOptions}
                  optionLabel='name'
                  optionValue='id'
                  display='chip'
                  filter
                  filterBy='name'
                  filterPlaceholder='Buscá por código o descripción'
                  resetFilterOnHide
                  emptyFilterMessage={searchingNomenclators ? 'Buscando…' : 'No hay nomencladores que coincidan'}
                  placeholder='Todos'
                  className={chipSelectClassName(errors.nomenclatorIds)}
                  onShow={() => { void searchNomenclators('') }}
                  onFilter={(event) => scheduleNomenclatorSearch(event.filter)}
                  onChange={(event) => changeNomenclators((event.value as number[] | null) ?? [])}
                  {...fieldA11y('nomenclatorIds', 'chd-nomenclators-error')}
                />
                <FieldError id='chd-nomenclators-error' message={errors.nomenclatorIds} />
              </div>

              <div className='chd-field'>
                <label htmlFor={FIELD_INPUT_IDS.buildingIds}>Edificios</label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.buildingIds}
                  value={form.buildingIds}
                  options={options.buildings}
                  optionLabel='name'
                  optionValue='id'
                  display='chip'
                  filter={options.buildings.length > 10}
                  filterBy='name'
                  resetFilterOnHide
                  showSelectAll
                  emptyFilterMessage='No hay edificios que coincidan'
                  placeholder='Todos'
                  className={chipSelectClassName(errors.buildingIds)}
                  onChange={(event) => updateField('buildingIds', (event.value as number[] | null) ?? [])}
                  {...fieldA11y('buildingIds', 'chd-buildings-error')}
                />
                <FieldError id='chd-buildings-error' message={errors.buildingIds} />
              </div>
            </div>

            <span className='chd-hint' aria-live='polite'>
              <i className='pi pi-info-circle' aria-hidden='true' />
              {combinations === 1
                ? 'Se va a crear 1 distribución.'
                : `Se van a crear ${combinations} distribuciones.`}
            </span>
            <FieldError id='chd-combinations-error' message={errors.combinations} />
          </>
        )}

        <h2 className='md-section-title'>Distribución</h2>
        <div className='chd-field'>
          <span id='chd-distribution-type-label' className='chd-field-label'>
            Tipo de distribución <span className='md-req'>*</span>
          </span>
          <div className='chd-radio-group' role='radiogroup' aria-labelledby='chd-distribution-type-label'>
            {options.distributionTypes.map((type) => (
              <div key={type.id} className='chd-radio-option'>
                <RadioButton
                  inputId={`chd-distribution-type-${type.id}`}
                  name='distributionType'
                  value={type.id}
                  checked={form.distributionTypeId === type.id}
                  className={errors.distributionTypeId ? 'p-invalid' : ''}
                  onChange={(event) => updateField('distributionTypeId', typeof event.value === 'number' ? event.value : null)}
                />
                <label htmlFor={`chd-distribution-type-${type.id}`}>{type.name}</label>
              </div>
            ))}
          </div>
          <FieldError id='chd-distribution-type-error' message={errors.distributionTypeId} />
        </div>

        {fixedAmountSelected ? (
          <div className='chd-row'>
            <div className='chd-field'>
              <label htmlFor={FIELD_INPUT_IDS.fixedAmount}>
                Importe fijo <span className='md-req'>*</span>
              </label>
              <InputNumber
                inputId={FIELD_INPUT_IDS.fixedAmount}
                value={form.fixedAmount}
                mode='decimal'
                locale='es-AR'
                minFractionDigits={2}
                maxFractionDigits={2}
                min={0}
                prefix='$ '
                placeholder='$ 0,00'
                className={errors.fixedAmount ? 'p-invalid' : ''}
                onValueChange={(event) => updateField('fixedAmount', typeof event.value === 'number' ? event.value : null)}
                onBlur={() => validateOnBlur('fixedAmount')}
                {...fieldA11y('fixedAmount', 'chd-fixed-amount-error')}
              />
              <FieldError id='chd-fixed-amount-error' message={errors.fixedAmount} />
            </div>
          </div>
        ) : (
          <div className='chd-row chd-row-fit'>
            {percentageField('honoPercentage', 'Porcentaje de honorarios', 'chd-hono-percentage-error')}
            {percentageField('copaymentPercentage', 'Porcentaje de coseguro', 'chd-copayment-percentage-error')}
            {percentageField('diferentialPercentage', 'Porcentaje de diferencial', 'chd-diferential-percentage-error')}
          </div>
        )}

        <h2 className='md-section-title'>Vigencia</h2>
        <div className='chd-row chd-row-fit'>
          <div className='chd-field'>
            <label htmlFor={FIELD_INPUT_IDS.validFrom}>
              Vigente desde <span className='md-req'>*</span>
            </label>
            <Calendar
              inputId={FIELD_INPUT_IDS.validFrom}
              value={form.validFrom}
              dateFormat='dd/mm/yy'
              showIcon
              placeholder='dd/mm/aaaa'
              className={errors.validFrom ? 'p-invalid' : ''}
              onChange={(event) => updateField('validFrom', event.value instanceof Date ? event.value : null)}
              onBlur={() => validateOnBlur('validFrom')}
              aria-invalid={errors.validFrom ? true : undefined}
              aria-describedby={errors.validFrom ? 'chd-valid-from-error' : undefined}
            />
            <FieldError id='chd-valid-from-error' message={errors.validFrom} />
          </div>

          <div className='chd-field'>
            <label htmlFor={FIELD_INPUT_IDS.validUntil}>Vigente hasta</label>
            <Calendar
              inputId={FIELD_INPUT_IDS.validUntil}
              value={form.validUntil}
              dateFormat='dd/mm/yy'
              showIcon
              showButtonBar
              minDate={form.validFrom ?? undefined}
              placeholder='Sin fecha'
              className={errors.validUntil ? 'p-invalid' : ''}
              onChange={(event) => updateField('validUntil', event.value instanceof Date ? event.value : null)}
              onBlur={() => validateOnBlur('validUntil')}
              aria-invalid={errors.validUntil ? true : undefined}
              aria-describedby={errors.validUntil ? 'chd-valid-until-error' : undefined}
            />
            <FieldError id='chd-valid-until-error' message={errors.validUntil} />
          </div>
        </div>

        {detail && (
          <span className='chd-hint'>
            <i className='pi pi-info-circle' aria-hidden='true' />
            {detail.status === 'FUTURE'
              ? 'Todavía no entró en vigencia: los cambios se guardan sobre esta misma distribución.'
              : 'Los valores actuales quedan en el historial. Con una fecha desde posterior a hoy, esta distribución se cierra el día anterior y se crea una nueva desde esa fecha.'}
          </span>
        )}

        {nothingCreated && (
          <div>
            <Alert
              color='yellow'
              icon='pi-exclamation-triangle'
              className='w-fit py-2 px-3'
              contentClassName='flex align-items-center'
            >
              <span className='text-yellow-700 m-0 text-l font-medium'>
                No se creó ninguna distribución: hay combinaciones que ya tienen una vigente o una que empieza ese día
              </span>
              <Button
                type='button'
                text
                rounded
                icon='pi pi-eye'
                className='chd-alert-action'
                aria-label='Ver combinaciones repetidas'
                tooltip='Ver combinaciones repetidas'
                tooltipOptions={{ position: 'top' }}
                onClick={() => setViewingDuplicates(true)}
              />
            </Alert>
          </div>
        )}

        <div className='chd-form-actions'>
          <Button
            type='button'
            className='button md-ghost'
            label='Cancelar'
            disabled={saving}
            onClick={requestLeave}
          />
          <Button
            type='submit'
            className='buttonMain'
            label={saving ? 'Guardando…' : (isEdition ? 'Guardar cambios' : 'Crear')}
            loading={saving}
          />
        </div>
      </form>

      <DuplicateCombinationsModal
        result={viewingDuplicates ? nothingCreated : null}
        onClose={() => setViewingDuplicates(false)}
      />

      <Modal
        visible={confirmingLeave}
        setVisible={setConfirmingLeave}
        header={<span className='chd-modal-title'>¿Salir sin guardar?</span>}
        width='w-medium'
        className='chd-modal'
        footerButtonLeftText='Seguir editando'
        onClickLeftBtn={() => setConfirmingLeave(false)}
        footerButtonRightText='Salir sin guardar'
        onClickRightBtn={() => {
          setConfirmingLeave(false)
          onBack()
        }}
      >
        <p className='chd-confirm-text'>Los cambios que hiciste se van a perder.</p>
      </Modal>
    </div>
  )
}
